/* eslint-disable react-hooks/exhaustive-deps */
// src/components/chatbot/Chatbot.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import chatbotImage from '../../assets/chatbotImage.jpg';
import ChatbotHeader from './chatbot/ChatbotHeader';
import ChatbotMessages from './chatbot/ChatbotMessages';
import ChatbotInput from './chatbot/ChatbotInput';
import ChatbotContact from './chatbot/ChatbotContact';
import api from '../../utils/api';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

export default function Chatbot() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [remainingMessages, setRemainingMessages] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);

  const chatbotContent = {
    companyName: "Citizen Hub",
    status: t('chatbot.widget.status'),
    welcomeMessage: t('chatbot.widget.welcome_message'),
    placeholder: t('chatbot.widget.placeholder'),
    phoneNumbers: ["(877) 800-8180", "(800) 950-0088"],
    buttons: {
      toggleContactInfoShow: t('chatbot.widget.buttons.show_contact'),
      toggleContactInfoHide: t('chatbot.widget.buttons.hide_contact'),
    },
    responses: {
      hello: t('chatbot.widget.responses.hello'),
      help: t('chatbot.widget.responses.help'),
      form: t('chatbot.widget.responses.form'),
      uscis: t('chatbot.widget.responses.uscis'),
      lawyer: t('chatbot.widget.responses.lawyer'),
      price: t('chatbot.widget.responses.price'),
      phone: t('chatbot.widget.responses.phone'),
      email: t('chatbot.widget.responses.email'),
      hours: t('chatbot.widget.responses.hours'),
      services: t('chatbot.widget.responses.services'),
      location: t('chatbot.widget.responses.location'),
      default: t('chatbot.widget.responses.default'),
    },
    contactInfo: {
      headline: t('chatbot.widget.contact.headline'),
      primaryAction: t('chatbot.widget.contact.primary_action'),
      alternateAction: t('chatbot.widget.contact.alternate_action'),
      emailAction: t('chatbot.widget.contact.email_action'),
      email: "support@citizenhub.us",
      servicesHeadline: t('chatbot.widget.contact.services_headline'),
      services: t('chatbot.widget.contact.services', { returnObjects: true }) || [],
      visitHeadline: t('chatbot.widget.contact.visit_headline'),
      address: t('chatbot.widget.contact.address'),
    },
  };

  const phoneNumbers = chatbotContent.phoneNumbers;

  const getDeviceId = () => {
    let id = localStorage.getItem('chatbot_device_id');
    if (!id) {
      id = 'guest_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('chatbot_device_id', id);
    }
    return id;
  };

  useEffect(() => {
    if (isOpen && !conversationId) {
      startConversation();
    }
  }, [isOpen]);

  const startConversation = async () => {
    setIsLoading(true);
    try {
      const headers = { 'X-Device-ID': getDeviceId() };
      const res = await api.get('/chat/conversation', { headers });

      if (res.data.success) {
        const conv = res.data.data.conversation;
        setConversationId(conv.id);
        setRemainingMessages(conv.remaining_messages ?? 10);

        const welcome = { type: 'bot', text: chatbotContent.welcomeMessage, timestamp: new Date() };
        setMessages([welcome]);

        // Load old messages
        const oldMsgs = res.data.data.messages.map(m => ({
          type: m.role === 'user' ? 'user' : 'bot',
          text: m.content,
          timestamp: new Date(m.created_at)
        }));
        setMessages(prev => [...oldMsgs, welcome]);
      }
    } catch (err) {
      console.error(err);
      setMessages([{ type: 'bot', text: t('chatbot.widget.connection_error') }]);
    } finally {
      setIsLoading(false);
    }
  };

  // === Send message ===
  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading || remainingMessages <= 0) return;

    const userMsg = { type: 'user', text: inputValue, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsLoading(true);

    try {
      const headers = { 'X-Device-ID': getDeviceId() };
      const res = await api.post('/chat/message', {
        conversation_id: conversationId,
        message: inputValue
      }, { headers });

      if (res.data.success) {
        const botMsg = {
          type: 'bot',
          text: res.data.data.message.content,
          timestamp: new Date(res.data.data.message.created_at)
        };
        setMessages(prev => [...prev, botMsg]);
        setRemainingMessages(res.data.data.remaining_messages);

        if (res.data.data.remaining_messages <= 0) {
          setTimeout(() => {
            Swal.fire({
              title: t('chatbot.widget.limit_reached_title'),
              text: t('chatbot.widget.limit_reached_text'),
              icon: "info",
              confirmButtonText: t('chatbot.widget.ok')
            });
          }, 600);
        }
      }
    } catch (err) {
      setMessages(prev => prev.slice(0, -1));
      Swal.fire(t('chatbot.swal.error_title'), t('chatbot.swal.send_message_error'), "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="chatbot-res fixed left-[2.5rem] bottom-32 z-50 w-[24rem] h-[500px] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          >
            <ChatbotHeader
              companyName={chatbotContent.companyName}
              status={chatbotContent.status}
              onClose={() => setIsOpen(false)}
            />

            <ChatbotMessages
              messages={messages}
              isLoading={isLoading}
              messagesEndRef={messagesEndRef}
              phoneNumbers={phoneNumbers}
            />

            <ChatbotInput
              inputValue={inputValue}
              setInputValue={setInputValue}
              onSend={sendMessage}
              isLoading={isLoading}
              disabled={remainingMessages === 0}
              placeholder={chatbotContent.placeholder}
            />

            <ChatbotContact
              show={showContactInfo}
              setShow={setShowContactInfo}
              contactInfo={chatbotContent.contactInfo}
              phoneNumbers={phoneNumbers}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle Button */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className="chatbot-icon fixed left-7 bottom-6 z-50 w-24 h-24 rounded-full"
      >
        <img src={chatbotImage} alt="Chat" className="w-full h-full object-contain rounded-full" />
      </motion.button>
    </>
  );
}